import { computed, ref, watchEffect, watch } from 'vue'
import { menuData, type MenuItem } from '@/config/menu'
import { usePermissions } from '@/composables/usePermissions'

// Type pour la structure attendue par le sidebar
type LegacyMenuItem = {
    key: string
    title: string
    icon?: string
    type: 'link' | 'sub' | 'empty'
    path?: string
    section: string
    children?: LegacyMenuItem[]
    active?: boolean
    selected?: boolean
}

// Clé pour le localStorage
const MENU_STATE_KEY = 'mydit_menu_state'

// Fonctions utilitaires pour le localStorage
const saveMenuStateToStorage = (menuItems: LegacyMenuItem[]) => {
    if (typeof window === 'undefined') return

    const stateMap: Record<string, { active: boolean, selected: boolean }> = {}

    const collectState = (items: LegacyMenuItem[]) => {
        items.forEach(item => {
            if (item.active || item.selected) {
                stateMap[item.key] = { active: item.active || false, selected: item.selected || false }
            }
            if (item.children) collectState(item.children)
        })
    }
    collectState(menuItems)

    try {
        localStorage.setItem(MENU_STATE_KEY, JSON.stringify(stateMap))
    } catch (e) {
        console.warn('Failed to save menu state to localStorage', e)
    }
}

const loadMenuStateFromStorage = (): Record<string, { active: boolean, selected: boolean }> => {
    if (typeof window === 'undefined') return {}

    try {
        const stored = localStorage.getItem(MENU_STATE_KEY)
        return stored ? JSON.parse(stored) : {}
    } catch (e) {
        console.warn('Failed to load menu state from localStorage', e)
        return {}
    }
}

export function useMenu() {
    const config = useRuntimeConfig()
    const { hasPermission, hasAnyRole, hasAnyGroup, isSuperAdmin } = usePermissions()

    const isItemAllowed = (item: MenuItem): boolean => {
        if(item.has_restriction_for_admin && isSuperAdmin.value)
            return false

        if (isSuperAdmin.value) return true

        const conds: boolean[] = []

        if (item.requirePermissionsAny?.length) {
            conds.push(item.requirePermissionsAny.some(a => hasPermission(a)))
        }
        if (item.requireRolesAny?.length) {
            conds.push(hasAnyRole(item.requireRolesAny))
        }
        if (item.requireGroupsAny?.length) {
            conds.push(hasAnyGroup(item.requireGroupsAny))
        }
        if (item.isVisible) {
            conds.push(item.isVisible({ hasPermission, hasAnyRole, hasAnyGroup, isSuperAdmin: isSuperAdmin.value }))
        }

        return conds.length === 0 ? true : conds.some(Boolean)
    }

    // Normalise pour comparaison (accents, casse)
    const normalizeForMatch = (s: string) =>
        s.toLowerCase().trim().normalize('NFD').replace(/\p{Diacritic}/gu, '')

    const filterMenu = (items: MenuItem[]): MenuItem[] => {
        // Même logique que Turnstile: chaîne brute en config, on parse ici (support string ou array pour compat)
        const val = config.public.hiddenMenuLabels
        const raw = Array.isArray(val)
            ? val.map((s: string) => String(s).trim()).filter(Boolean)
            : (typeof val === 'string' ? val.split(',').map((s: string) => s.trim()).filter(Boolean) : [])
        const hiddenNormalized = new Set(raw.map((l: string) => normalizeForMatch(l)))
        return items
            .map(i => {
                // Cacher les menus dont le label est dans hiddenMenuLabels (configurable via env)
                if (hiddenNormalized.size > 0 && hiddenNormalized.has(normalizeForMatch(i.label))) return null
                const children = i.children ? filterMenu(i.children) : undefined
                const selfOk = isItemAllowed(i)
                const allowed = selfOk || (children && children.length > 0)
                return allowed ? { ...i, children } : null
            })
            .filter(Boolean) as MenuItem[]
    }

    // Convertit la nouvelle structure vers l'ancienne structure attendue par le sidebar
    const convertToLegacyFormat = (items: MenuItem[], section: string, parentKey = ''): LegacyMenuItem[] => {
        return items.map((item, index) => {
            // Générer une clé unique basée sur le label pour une meilleure stabilité
            let key = item.label.toLowerCase().replace(/\s+/g, '-').replace(/[&]/g, '')
            if (parentKey) {
                key = `${parentKey}-${key}`
            }

            const hasChildren = item.children && item.children.length > 0
            let children: LegacyMenuItem[] | undefined = undefined

            if (hasChildren) {
                children = convertToLegacyFormat(item.children!, section, key)
                
                // Si un menu a exactement un seul enfant visible, le transformer en lien direct
                // (évite d'avoir un sous-menu avec un seul élément)
                if (children.length === 1) {
                    const singleChild = children[0]
                    return {
                        key: singleChild.key,
                        title: item.label, // Garder le label du parent
                        icon: item.icon || singleChild.icon, // Préférer l'icône du parent si disponible
                        type: 'link',
                        path: singleChild.path,
                        section,
                        active: false,
                        selected: false
                    }
                }
            }

            const legacyItem: LegacyMenuItem = {
                key,
                title: item.label,
                icon: item.icon,
                type: hasChildren && children && children.length > 1 ? 'sub' : 'link',
                path: item.to,
                section,
                active: false,
                selected: false
            }

            if (hasChildren && children && children.length > 1) {
                legacyItem.children = children
            }

            return legacyItem
        })
    }

    // Fonction pour préserver l'état (active/selected) d'un menu
    const preserveMenuState = (oldItems: LegacyMenuItem[], newItems: LegacyMenuItem[]) => {
        const stateMap = new Map<string, { active: boolean, selected: boolean }>()

        // Collecter l'état actuel
        const collectState = (items: LegacyMenuItem[]) => {
            items.forEach(item => {
                if (item.active || item.selected) {
                    stateMap.set(item.key, { active: item.active || false, selected: item.selected || false })
                }
                if (item.children) collectState(item.children)
            })
        }
        collectState(oldItems)

        // Appliquer l'état aux nouveaux items
        const applyState = (items: LegacyMenuItem[]) => {
            items.forEach(item => {
                const state = stateMap.get(item.key)
                if (state) {
                    item.active = state.active
                    item.selected = state.selected
                }
                if (item.children) applyState(item.children)
            })
        }
        applyState(newItems)
    }

    // Fonction pour construire le menu
    const buildMenu = (existingState?: LegacyMenuItem[]): LegacyMenuItem[] => {
        const filtered = filterMenu(menuData)

        // Convertir vers le format legacy et organiser par sections
        const SECTION_ORDER = ["general", "academie", "admin"]
        const SECTION_LABELS = {
            general: "GÉNÉRAL",
            academie: "ACADÉMIE",
            admin: "ADMINISTRATION",
        }

        const groups = new Map<string, LegacyMenuItem[]>()

        // Grouper les items par section
        filtered.forEach(item => {
            // Déterminer la section basée sur le contenu
            let section = "general"
            if (item.label.includes('Scolarité') || item.label.includes('Planning') || item.label.includes('Notes') || item.label.includes('Maquettes') || item.label.includes('Délibération') || item.label.includes('Fin d')) {
                section = "academie"
            } else if (item.label.includes('Équipements') || item.label.includes('Personnel') || item.label.includes('Structure') || item.label.includes('Rapports') || item.label.includes('Paramètres')) {
                section = "admin"
            }

            if (!groups.has(section)) groups.set(section, [])
            groups.get(section)!.push(...convertToLegacyFormat([item], section))
        })

        // Construire le menu final avec les catégories
        const out: LegacyMenuItem[] = []
        for (const sec of SECTION_ORDER) {
            const items = groups.get(sec) || []
            if (items.length === 0) continue

            // Ajouter la catégorie
            out.push({
                key: `cat_${sec}`,
                title: SECTION_LABELS[sec as keyof typeof SECTION_LABELS] || sec.toUpperCase(),
                type: 'empty',
                section: sec,
                menuTitle: SECTION_LABELS[sec as keyof typeof SECTION_LABELS] || sec.toUpperCase()
            } as any)

            // Ajouter les items
            out.push(...items)
        }

        // Préserver l'état du menu existant
        if (existingState && existingState.length > 0) {
            preserveMenuState(existingState, out)
        } else {
            // Premier chargement : charger l'état depuis le localStorage
            const savedState = loadMenuStateFromStorage()
            const applyState = (items: LegacyMenuItem[]) => {
                items.forEach(item => {
                    const state = savedState[item.key]
                    if (state) {
                        item.active = state.active
                        item.selected = state.selected
                    }
                    if (item.children) applyState(item.children)
                })
            }
            applyState(out)
        }

        return out
    }

    // État réactif pour maintenir les références aux objets du menu
    // Initialiser IMMÉDIATEMENT de manière synchrone avec le localStorage
    const menuState = ref<LegacyMenuItem[]>(buildMenu())

    // Fonction pour sauvegarder l'état du menu
    const saveMenuState = () => {
        saveMenuStateToStorage(menuState.value)
    }

    // Mettre à jour le menu quand les permissions changent
    // mais seulement si la STRUCTURE du menu change, pas les états active/selected
    watchEffect(() => {
        const newMenu = buildMenu(menuState.value)

        // Fonction helper pour comparer la structure sans les états
        const getStructureSignature = (items: LegacyMenuItem[]): string => {
            const simplify = (item: LegacyMenuItem): any => ({
                key: item.key,
                title: item.title,
                path: item.path,
                type: item.type,
                children: item.children ? item.children.map(simplify) : undefined
            });
            return JSON.stringify(items.map(simplify));
        };

        const oldStructure = getStructureSignature(menuState.value);
        const newStructure = getStructureSignature(newMenu);

        // Ne mettre à jour que si la structure a changé
        if (oldStructure !== newStructure) {
            menuState.value = newMenu;
        }
    })

    const allowedMenu = computed(() => menuState.value)

    return { allowedMenu, saveMenuState }
}
